/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Matching methods", description = "Understand how Match Booster matches consumer data, work data, address data, and anonymous visitor data against its identity graph.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Matching methods",
        description: "Understand how Match Booster matches consumer data, work data, address data, and anonymous visitor data against its identity graph.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Consumer data",
          "depth": 0,
          "id": "consumer-data"
        }, {
          "value": "Work data (B2B)",
          "depth": 0,
          "id": "work-data-b2b"
        }, {
          "value": "Address data",
          "depth": 0,
          "id": "address-data"
        }, {
          "value": "Anonymous web visitor data (IP address)",
          "depth": 0,
          "id": "anonymous-web-visitor-data-ip-address"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, marketers"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Match Booster takes your first-party identifiers across multiple data types and resolves them to people and devices in its identity graph, so ad platforms can match more of your records."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster supports four types of input data, each suited to different use cases. The type of data you provide determines which records can be matched and what enriched identifiers Match Booster can append."
      }), "\n", _jsxs(_components.p, {
        children: ["For details on configuring these inputs, see ", _jsx(_components.a, {
          href: "/match-booster/configure-model#map-match-keys",
          children: "Map match keys"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "consumer-data",
        children: "Consumer data"
      }), "\n", _jsx(_components.p, {
        children: "Consumer data matching enriches your first-party records with additional emails, phone numbers, device IDs, and cookie IDs. Use this method when you collect emails or phone numbers from consumers and want ad platforms to match more of those records."
      }), "\n", _jsx(_components.p, {
        children: "This is the most common matching method and works well for targeting, suppression, and lookalike campaigns."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "work-data-b2b",
        children: "Work data (B2B)"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster matches B2B employees to consumer profiles and devices. If you collect work emails from prospects or have purchased a list of business contacts, work data matching enriches those records with consumer emails, phone numbers, device IDs, and cookie IDs that ad platforms are more likely to match on."
      }), "\n", _jsx(_components.p, {
        children: "This is useful when your first-party data is primarily work-context identifiers that have low match rates in consumer-oriented ad platforms."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "address-data",
        children: "Address data"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster matches first-party name and address data to person-level identifiers in its identity graph, including hashed emails, phone numbers, and device IDs."
      }), "\n", _jsxs(_components.p, {
        children: ["To use address matching, provide at minimum: first name, last name, first address line, and postal code. Including city and state improves accuracy. See ", _jsx(_components.a, {
          href: "/match-booster/configure-model#match-keys-reference",
          children: "Match keys reference"
        }), " for format requirements."]
      }), "\n", _jsxs(_components.p, {
        children: ["For US enrichment, you can provide SHA256 hashes of name and address fields instead of plain text. Hashed values only match exactly, so they typically produce fewer matches than plain text. See ", _jsx(_components.a, {
          href: "/match-booster/configure-model#hashed-names-and-addresses",
          children: "Hashed names and addresses"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "anonymous-web-visitor-data-ip-address",
        children: "Anonymous web visitor data (IP address)"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster matches anonymous visitor IP addresses to known consumers and devices using reverse IP lookup. When you provide a first-party IP address, Hightouch filters the data to verify it comes from a residential IP address. If it does, Hightouch's identity graph matches that IP address to the people and devices associated with that household and sends those identifiers to ad platforms."
      }), "\n", _jsx(_components.p, {
        children: "This method is designed for web retargeting scenarios where you have visitor session data with IP addresses but no known user identifiers."
      })]
    });
  }
}
export default MDXContent;
